import React, {useRef} from 'react';
import {FileUpload, FileUploadHeaderTemplateOptions, FileUploadSelectEvent} from "primereact/fileupload";
import {Button} from "primereact/button";
import {Tag} from "primereact/tag";

interface FileUploaderProps {
    formData: any;
    setFormData: React.Dispatch<React.SetStateAction<any>>;
    index: number;
}

const FileUploader = ({formData, setFormData, index}: FileUploaderProps) => {
    // const [totalSize, setTotalSize] = useState(0);
    const fileUploadRef = useRef<FileUpload>(null);

    const currentDoc = formData?.documents?.[index];
    const currentFile = currentDoc?.document;

    const handleFileDataChange = (file: File | null) => {
        const updatedDocuments = [...(formData.documents || [])];
        if (updatedDocuments[index]) {
            updatedDocuments[index] = {
                ...updatedDocuments[index],
                document: file
            };
            setFormData({...formData, documents: updatedDocuments});
        }
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const onTemplateSelect = (e: FileUploadSelectEvent) => {
        // setTotalSize(e.files[0].size || 0);
        handleFileDataChange(e.files[0]);
    };

    const onTemplateClear = () => {
        // setTotalSize(0);
        handleFileDataChange(null);
        if (fileUploadRef.current) {
            fileUploadRef.current.setFiles([]);
        }
    };

   

    const headerTemplate = (options: FileUploadHeaderTemplateOptions) => {
        const {className, chooseButton} = options;
        return (
            <div className={className}
                 style={{backgroundColor: 'transparent', display: 'flex', alignItems: 'center', padding: '0.5rem'}}>
                {chooseButton}
                <Button
                    icon="pi pi-times"
                    className="p-button-danger p-button-rounded p-button-outlined ml-2"
                    style={{width: '2rem', height: '2rem'}}
                    disabled={!currentFile}
                    onClick={onTemplateClear}
                    type="button"
                />
            </div>
        );
    };

    const itemTemplate = (file: object) => {
        const fileRef = file as File;
        const fileSize = formatFileSize(fileRef.size || 0);

        // Determine the type of file
        const isImage = fileRef.type.startsWith('image/');
        const isPdf = fileRef.type === 'application/pdf';

        const renderFilePreview = () => {
            if (isImage) {
                return (
                    <img
                        alt={fileRef.name}
                        role="presentation"
                        src={(fileRef as any).objectURL} // PrimeReact FileUpload adds objectURL automatically
                        width={40}
                        height={40}
                        className="flex-shrink-0 border-round"
                        style={{objectFit: 'cover'}}
                    />
                );
            }

            if (isPdf) {
                return (
                    <i className="pi pi-file-pdf text-red-500 flex-shrink-0" style={{fontSize: '2rem'}}></i>
                );
            }

            // Default icon for other file types
            return (
                <i className="pi pi-file text-primary flex-shrink-0" style={{fontSize: '2rem'}}></i>
            );
        };

        return (
            <div className="flex align-items-center w-full p-2 ">
                <div className="flex align-items-center" style={{flex: '1', minWidth: '0'}}>
                    {renderFilePreview()}

                    <div className="flex flex-column ml-3" style={{minWidth: '0'}}>
                    <span className="font-semibold text-900" style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: 'block'
                    }}>
                        {fileRef.name}
                    </span>
                        <small className="text-secondary md:hidden">Size: {fileSize}</small>
                    </div>
                </div>

                <div className="flex align-items-center ml-auto">
                    <Tag value={fileSize} severity="warning" className="hidden md:block"/>
                </div>
            </div>
        );
    };

    const emptyTemplate = () => (
        <div className="flex align-items-center flex-column py-2">
            <i className="pi pi-upload p-2" style={{fontSize: '1.5rem', color: 'var(--surface-d)'}}></i>
            <span style={{fontSize: '0.85rem', color: 'var(--text-color-secondary)'}}>
                Upload or Drag & Drop {currentDoc?.document_type_id?.name || 'File'}
            </span>
        </div>
    );

    return (
        <FileUpload
            ref={fileUploadRef}
            mode="advanced"
            multiple={false}
            accept="image/*,application/pdf"
            maxFileSize={2000000}
            onSelect={onTemplateSelect}
            headerTemplate={headerTemplate}
            itemTemplate={itemTemplate}
            emptyTemplate={emptyTemplate}
            contentStyle={{padding: '0.5rem', minHeight: '80px'}}
            chooseOptions={{
                icon: 'pi pi-fw pi-file',
                iconOnly: true,
                className: 'p-button-rounded p-button-outlined',
                style: {width: '2rem', height: '2rem'}
            }}
        />
    );
};

export default FileUploader;